import type { List, ListKind } from "../lib/api.js";
import type { Store } from "../data/store.js";
import { rememberUser, type User } from "../lib/auth.js";
import type { AccountFacet } from "./settings.js";
import { el } from "../lib/dom.js";
import { environmentChip } from "../lib/environment.js";
import { keyboardViewport, opensKeyboard } from "../lib/keyboardViewport.js";
import { holdsText } from "../lib/update.js";
import { icon } from "../lib/icons.js";
import { todayIso } from "../lib/format.js";
import { uncheckedByList, uncheckedPhrase } from "../lib/counts.js";
import { inToday } from "../lib/projection.js";
import { suggestCategories } from "./categorySuggestions.js";
import { SHARED_WITH_ME } from "./sharedWithMe.js";
import { addNavigationGuard, addedPath, agendaPath, back, listPath, onNavigationCommit, pushEntry, routeListId, screenKey, selection, type Route } from "../lib/router.js";
import {
  CONFIRM_INERT_MS,
  KIND_ICON,
  confirmPanel,
  countBadge,
  listLabel,
  logo,
  setCount,
  syncLine,
  syncMark,
  syncNotices,
  syncWords,
  type Asked,
  type ConfirmOptions,
  type ConfirmSpec,
  type DetailHost,
  type DraftGuard,
  type Popup,
  type PopupSpec,
  type PopupStep,
  type Screen,
  type ScreenAction,
} from "./ui.js";

/**
 * The frame every signed-in screen sits inside.
 *
 * Until now each screen drew its own chrome — the index carried the brand and
 * the theme switch, the list screen a back link — which meant the switch was
 * reachable from exactly one screen and the app had no navigation at all. The
 * design system's shell is one frame with the screen mounted into it: a violet
 * rail from 768px up, a fixed bottom navigation below that, and a detail
 * surface whose container changes with the width.
 *
 * Nothing here reads the store except the rail, and the rail reads it for two
 * things: which lists exist, and how many unchecked items each holds.
 */

/**
 * Above this the plus sits beside the screen's heading; below it, it floats
 * above the bottom navigation. The same 768 as `shell.css`'s rail breakpoint,
 * and kept in step with it: a plus styled as floating while it sits in a
 * heading is a 56px button in a 44px row.
 */
const RAIL = "(min-width: 768px)";

/**
 * Whether the detail panel can dock beside the list (g55): the width the
 * workspace actually has, after the rail and the scrollbar's lane, against
 * the room a usable centre pane and the inspector need together. Both are
 * measured rather than assumed from the viewport, so enlarged text, which
 * widens the pane's floor, and a wider rail are answered by the same rule. A
 * measurement of nothing docks nothing.
 */
export function dockFits(available: number, need: number): boolean {
  return need > 0 && available >= need - 0.5;
}

/**
 * What a panel or popup focuses when it opens and names nothing itself: its
 * first control. A link in a rendered note (g76) is part of what is read,
 * not a control, so it never takes the focus on open: a view link's Details
 * would otherwise start a screen reader partway through the note.
 */
function firstControl(root: HTMLElement): HTMLElement | null {
  return [...root.querySelectorAll<HTMLElement>("input, textarea, select, button, a[href]")]
    .find((node) => !node.closest(".note-body")) ?? null;
}

export interface Chrome {
  updateUser(user: User): void;
  /** The account's actions, for the Settings page. See `AccountFacet`. */
  account: AccountFacet;
  node: HTMLElement;
  detail: DetailHost;
  /**
   * Says something once, politely, in the app's one live region.
   *
   * Every message a screen shows goes through here as well as into its visible
   * slot; see the region itself for why there is exactly one and why it is not
   * the slot.
   */
  announce(message: string): void;
  /** Puts a screen in the column. The frame around it is not rebuilt. */
  mount(screen: Screen, route: Route): void;
  /** Reflects a route that did not change screens — a selection, say. */
  setRoute(route: Route): void;
  /** See `Shell.openPopup`. */
  openPopup(spec: PopupSpec): Popup;
  /** See `Shell.confirm`. */
  confirm(spec: ConfirmSpec, options?: ConfirmOptions): Asked;
  /** See `Shell.refreshAction`. */
  refreshAction(): void;
  /** See `Shell.narrow`. */
  narrow(): boolean;
  /** See `Shell.onNarrow`. */
  onNarrow(listener: (narrow: boolean) => void): () => void;
  /**
   * True while a surface the frame owns holds something a reload would lose:
   * a Details draft, the popup's draft, or a discard question up over either
   * -- the question is a draft whose fate is still being decided. Read with
   * the screen's own `dirty()` before a service-worker update is let through.
   */
  dirty(): boolean;
  navigationDirty(): boolean;
  navigationBlocked(): boolean;
  cancelQuestion(): void;
  askDeparture(proceed: () => void, cancel: () => void): () => void;
  /** See `Shell.lend`. */
  lend(store: Store): () => void;
  dispose(): void;
}

interface Deps {
  store: Store;
  user: User;
  reauth(): void;
  deleteAccount?(): Promise<void>;
  /**
   * False for a page with nowhere to go (g72): a shared list's own page has
   * no account, so no rail, no bottom navigation, no drawer and no Settings.
   * The column, the live region, the sync notices, Details, the popup host
   * and the plus are all as they are in the account's frame.
   */
  navigation?: false;
}

/** The frame for a shared list's page (g72): the account's, less everything that leads into an account. */
export function createStandaloneChrome(deps: { store: Store; reauth(): void }): Chrome {
  return createChrome({ ...deps, user: { id: "", email: "" }, navigation: false });
}

export function createChrome(deps: Deps): Chrome {
  const { store } = deps;
  const standalone = deps.navigation === false;
  const htmlRoot = document.documentElement;

  const column = el("main", { class: "column", attrs: { id: "main" } });
  let lastDraftFocus: HTMLElement | undefined;
  column.addEventListener('focusin', event => {
    if (event.target instanceof HTMLElement && event.target.matches('input, textarea, select'))
      lastDraftFocus = event.target;
  });
  const inspector = el("aside", {
    class: "inspector",
    attrs: { hidden: true },
  });
  /**
   * Where every message this app shows is also *said*.
   *
   * One region, mounted once and never replaced, because that is the only shape
   * that works: a live region has to be in the accessibility tree before its
   * text changes, so a region built around the message — which is what
   * `banner()` used to do — is a new element with nothing to compare against,
   * and a slot hidden with `display: none` until it has something in it is not
   * in the tree either. This one is always there and always empty until it
   * is not.
   *
   * Absolutely positioned, via `.visually-hidden`, so it is not a flex item and
   * costs no gap on the screens it sits in. That is not a detail: the first
   * attempt at this kept the visible slot in the flow instead and pushed every
   * screen down by 37px.
   */
  const announcer = el("p", { class: "visually-hidden", attrs: { role: "status" } });
  const announce = (message: string) => {
    // Cleared first, because a screen reader says nothing when the text it is
    // handed is the text already there — and failing the same write twice is
    // an ordinary thing to do.
    announcer.textContent = "";
    announcer.textContent = message;
  };
  const notices = syncNotices(store, deps.reauth, announce);
  // g79: every Category field, in Add, Details or a popup, names this one list.
  const categories = suggestCategories(store);
  /**
   * A screen reading a store of its own (g137): the Category fields suggest
   * from it, as they do on its own page, and its sync notices sit with the
   * account's, so a refused write or a failed sync still says so and offers
   * the way out. Handed back as the screen goes.
   */
  let lent: { categories: Screen & { node: HTMLDataListElement }; notices: Screen } | undefined;
  const giveBack = (): void => {
    if (!lent) return;
    lent.categories.node.replaceWith(categories.node);
    lent.categories.dispose();
    lent.notices.node.remove();
    lent.notices.dispose();
    lent = undefined;
  };
  function lend(other: Store): () => void {
    giveBack();
    const mine = { categories: suggestCategories(other), notices: syncNotices(other, deps.reauth, announce) };
    lent = mine;
    categories.node.replaceWith(mine.categories.node);
    notices.node.after(mine.notices.node);
    return () => { if (lent === mine) giveBack(); };
  }

  const workspace = el("div", {
    class: "workspace",
    children: [announcer, notices.node, categories.node, column, inspector],
  });

  /**
   * The layout's own thresholds, measured (g55). A script cannot read a token
   * as pixels: `getPropertyValue` hands back `37.5rem` or a `calc()`. So the
   * stylesheet sizes this box from the tokens and the shell reads the box. Its
   * width is the centre pane's floor plus the inspector, and its height the
   * shortest window the rail is kept for. Both are in `rem` where they should
   * be, so 200% text moves them with the words.
   */
  const probe = el("div", { class: "layout-probe", attrs: { "aria-hidden": "true" } });

  // ---- the detail surface -------------------------------------------------

  /**
   * One panel, and the two places it can be.
   *
   * A `<dialog>` where the list and the panel cannot sit side by side, rather
   * than a hand-built overlay: the focus trap, Escape, the backdrop and
   * inerting the page behind it are all behaviours that have to be exactly
   * right and are already written. Where they can (`dockFits`), the panel is a
   * column, so the dialog is closed and the same node is moved into it —
   * moved, because appending a node does not copy it, and two prebuilt copies
   * of one panel is how a form ends up collecting what nobody sees.
   */
  const heading = el("h2", { class: "detail-title", attrs: { id: "detail-title" } });
  const closeDetail = el("button", {
    class: "detail-close",
    attrs: { type: "button", "aria-label": "Close" },
    children: [icon("close")],
  });
  // The slot rather than the panel: `present` replaces what is in here, and the
  // surface around it — heading, close button — is built once and moved whole.
  const panelSlot = el("div", { class: "detail-panel" });
  const surface = el("div", {
    class: "detail-surface",
    children: [el("div", { class: "detail-head", children: [heading, closeDetail] }), panelSlot],
  });

  const dialogBody = el("div", { class: "detail-body" });
  const dialog = el("dialog", {
    class: "detail-dialog",
    // The visible job title can differ from the item-specific accessible name.
    children: [dialogBody],
  });

  let panel: HTMLElement | undefined;
  let cancelDeparture: (() => void) | undefined;
  let onDismiss: (() => void) | undefined;
  let returnFocus: HTMLElement | (() => HTMLElement | undefined) | undefined;
  let guard: DraftGuard | undefined;
  let label = "";
  let title = "";
  function nameDetail(visibleTitle: string, accessibleName: string): void {
    heading.textContent = visibleTitle;
    dialog.setAttribute("aria-label", accessibleName);
    inspector.setAttribute("aria-label", accessibleName);
  }
  // The question standing in for the panel, while there is one — see
  // `confirmDetail` — and where the focus was when it went up.
  let confirmNode: HTMLElement | undefined;
  let heldFocus: HTMLElement | undefined;
  // The question's safe answer, while one is up. Every exit the host owns
  // gives it while the question stands: Escape on "Delete 'Milk'?" is a No,
  // not a request for a second question about the draft underneath (D09).
  let confirmKeep: (() => void) | undefined;
  // Set by `cancel` when it answered a question, and read by the `close` net
  // below: Chrome may close the dialog after a cancel it would not let be
  // prevented, and by then the question is gone and the draft may be clean —
  // nothing left to say the surface should stay, except this.
  let answered = false;
  // The last control in the panel to hold the focus. Read on `focusin`
  // rather than from `document.activeElement` when the question goes up,
  // because a real click on Close has already moved the focus to Close by
  // the time its handler runs — a synthetic `.click()` in the suite does
  // not, which is how this was first written the wrong way round.
  let lastPanelFocus: HTMLElement | undefined;
  panelSlot.addEventListener("focusin", (event) => {
    const target = event.target;
    if (panel && target instanceof HTMLElement && panel.contains(target)) lastPanelFocus = target;
    if (target instanceof HTMLElement && target.matches('input, textarea, select')) lastDraftFocus = target;
  });
  // `close` fires for a dialog closed by Escape and for one closed by the code
  // moving the panel into the inspector. Only the first is the person saying
  // they are done with it. Counted rather than flagged (g55): a browser fires
  // `close` from a queued task, after `close()` has returned, so a flag set
  // around the call was already clear when the event came. A dirty draft
  // moved into the inspector then read as a forced close and put an empty
  // modal back up over the page.
  let quietCloses = 0;

  /** Whether the panel docks at the size the page has now. See `dockFits`. */
  const docks = () => dockFits(workspace.getBoundingClientRect().width, probe.getBoundingClientRect().width);
  // The answer the panel was last placed by, so a change of size re-places it
  // only when the answer changes.
  let docked = false;

  function closeDialog(): void {
    if (!dialog.open) return;
    quietCloses += 1;
    dialog.close();
  }

  function focusPanel(): void {
    // The question, while one is up: its safe answer is what should hold the
    // focus, and the panel's controls are hidden behind it.
    if (confirmNode) {
      confirmNode.querySelector<HTMLElement>("button")?.focus();
      return;
    }
    if (panel) firstControl(panel)?.focus();
  }

  /** The focus back where it was in the surface, after the dialog blinked. */
  function refocusPanel(): void {
    if (!confirmNode && lastPanelFocus?.isConnected) lastPanelFocus.focus({ preventScroll: true });
    else focusPanel();
  }

  /**
   * A confirmation as a state of this surface (E05, and g08's Delete).
   *
   * The panel is hidden, not swapped out: the editor keeps a store
   * subscription that lets go the moment its form is disconnected, and a
   * panel that stopped following the list while a question was up would come
   * back with Move up wrong. The popup host detaches its body instead, and
   * can, because the Add form holds its subscriptions until it closes.
   */
  function confirmDetail(spec: ConfirmSpec): void {
    cancelDeparture?.();
    if (!panel) return;
    // Never one question under another: a standalone question up now -- a
    // row's Delete -- is closed first. It holds no draft, and closing it is
    // its safe answer; the person can ask it again.
    if (alone?.isOpen()) alone.close();
    confirmNode?.remove();
    const built = confirmPanel(spec);
    // Remembered so `resume` can put the focus back where the typing was; a
    // second question over the first keeps the first's answer.
    if (!confirmNode) heldFocus = lastPanelFocus;
    confirmNode = built.node;
    confirmKeep = spec.keep.onChoose;
    panel.hidden = true;
    panelSlot.append(built.node);
    nameDetail(spec.title, spec.title);
    built.focus.focus({ preventScroll: true });
  }

  function resumeDetail(): void {
    if (!panel || !confirmNode) return;
    confirmNode.remove();
    confirmNode = undefined;
    confirmKeep = undefined;
    panel.hidden = false;
    nameDetail(title, label);
    const back = heldFocus;
    heldFocus = undefined;
    if (back?.isConnected) back.focus({ preventScroll: true });
    else focusPanel();
  }

  /** Now when nothing changed; otherwise the question first, and on Discard. */
  function guardedDetail(proceed: () => void): void {
    if (!panel || !guard?.dirty()) {
      proceed();
      return;
    }
    confirmDetail({
      title: guard.title,
      message: guard.message,
      keep: { label: guard.keep, onChoose: resumeDetail },
      act: { label: guard.discard, onChoose: proceed },
    });
  }

  /**
   * The unguarded dismissal. The dialog comes down first and quietly — counted
   * in `quietCloses`, so the `close` listener below does not report it a second
   * time — and then the screen is told, which is the order Close has always
   * produced: the surface gone, then the navigation undone.
   */
  function dismissNow(): void {
    closeDialog();
    onDismiss?.();
  }

  /**
   * The guarded way out, and the one every exit the host owns goes through.
   *
   * With a question already up, the exit answers it the safe way and stops
   * there. Running the guard instead would put the discard question over a
   * Delete one — a second prompt for someone who pressed Escape to say no.
   */
  function dismissDetail(): void {
    if (confirmNode) {
      confirmKeep?.();
      return;
    }
    guardedDetail(dismissNow);
  }

  /**
   * The answer, said on the frame for the stylesheet and the browser
   * drivers, and written only when it changes, so a driver counting changes
   * counts decisions rather than observations.
   */
  function sayDetail(next: boolean): void {
    const value = next ? "docked" : "modal";
    if (node.dataset["detail"] !== value) node.dataset["detail"] = value;
  }

  function place(): void {
    docked = docks();
    sayDetail(docked);
    workspace.classList.toggle("with-inspector", docked && panel !== undefined);

    if (!panel) return;

    if (docked) {
      closeDialog();
      dialogBody.replaceChildren();
      inspector.replaceChildren(surface);
      inspector.hidden = false;
      return;
    }

    inspector.hidden = true;
    inspector.replaceChildren();
    dialogBody.replaceChildren(surface);
    // Parked while a popup is up (see `openPopup`), because two modals are
    // never stacked. Closing the popup places the panel again, and the sheet
    // comes back then. jsdom has no `showModal`; the test setup shims it.
    // Guarded anyway so a browser that somehow lacks it shows the panel
    // rather than nothing.
    if (!dialog.open && !popupDialog.open) dialog.showModal?.();
    syncActionHidden();
  }

  const detail: DetailHost = {
    persistent: docks,

    present(next, options) {
      keyboard.reset();
      // A new panel ends any question about the old one: the row switch's
      // Discard lands here, and so does a rebuild for a kind change.
      confirmNode = undefined;
      confirmKeep = undefined;
      answered = false;
      heldFocus = undefined;
      lastPanelFocus = undefined;
      panel = next;
      onDismiss = options.onDismiss;
      returnFocus = options.returnFocus;
      guard = options.guard;
      label = options.label;
      title = options.title ?? options.label;
      nameDetail(title, label);
      panelSlot.replaceChildren(next);
      place();
      focusPanel();
    },

    dirty: () => panel !== undefined && (guard?.dirty() ?? false),
    asking: () => confirmNode !== undefined,
    guarded: guardedDetail,
    dismiss: dismissDetail,
    confirm: confirmDetail,
    resume: resumeDetail,

    clear() {
      cancelDeparture?.();
      // A question up counts as the surface holding the focus: its buttons
      // are the only live controls, and the one just pressed is disabled by
      // now, which some browsers answer by dropping the focus to the body.
      const held = surface.contains(document.activeElement) || confirmNode !== undefined;
      panel = undefined;
      label = "";
      title = "";
      heading.textContent = "";
      dialog.removeAttribute("aria-label");
      inspector.removeAttribute("aria-label");
      onDismiss = undefined;
      guard = undefined;
      confirmNode = undefined;
      confirmKeep = undefined;
      answered = false;
      heldFocus = undefined;
      lastPanelFocus = undefined;
      panelSlot.replaceChildren();
      closeDialog();
      dialogBody.replaceChildren();
      inspector.replaceChildren();
      inspector.hidden = true;
      workspace.classList.remove("with-inspector");
      // Only when the panel had the focus: taking it from somewhere else
      // because a panel closed is how a keyboard user loses their place.
      // Resolved now, not at `present`: the control that opened the panel may
      // have gone with the row it was on.
      const target = typeof returnFocus === "function" ? returnFocus() : returnFocus;
      if (held && target?.isConnected) target.focus();
      returnFocus = undefined;
      syncActionHidden();
    },
  };

  // Escape. Through `cancel` rather than `close`, because `cancel` is the one
  // that can be prevented: a dialog holding a draft says no and stays up.
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    answered = confirmNode !== undefined;
    dismissDetail();
  });

  /**
   * A close the host did not make — the same safety net as the popup's.
   *
   * Chrome honours `preventDefault` on `cancel` only when there has been user
   * activation since the last one, so a second Escape in a row closes the
   * dialog regardless. A draft, or a question, is still worth keeping: the
   * surface comes straight back up — and in the state `cancel` left it,
   * because `cancel` still fires first and has already given the Escape its
   * meaning (asked, or answered the question). Asking again here put the
   * question back over a form the person had just returned to; the real
   * browser pass caught it.
   */
  dialog.addEventListener("close", () => {
    syncActionHidden();
    if (quietCloses > 0) {
      quietCloses -= 1;
      return;
    }
    const keep = guard?.dirty() || confirmNode !== undefined || answered;
    answered = false;
    if (keep) {
      dialog.showModal?.();
      refocusPanel();
      return;
    }
    onDismiss?.();
  });

  // A click that lands on the dialog element itself is a click on the backdrop:
  // everything inside it is a child.
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dismissDetail();
  });

  // Escape, the backdrop and this button are one guarded path rather than
  // three that can disagree — and the same one in the inspector, where there
  // is no dialog to close.
  closeDetail.addEventListener("click", dismissDetail);

  /**
   * A change of size that moves the panel between the dialog and the column
   * (g55). The draft is the same node either way; what the move would lose is
   * the rest of the person's place. The field they were typing in gets the
   * focus back, rather than the panel's first control getting it; its caret
   * is the field's own state and survives the move, in Chromium as in jsdom
   * (a mutation that dropped an explicit restore went uncaught by both). The
   * list keeps the selected row, or with none the first row in view, where it
   * was on screen, since the column narrows or widens under it.
   */
  function onLayout(): void {
    const next = docks();
    sayDetail(next);
    if (next === docked) return;
    const active = document.activeElement;
    const held = active instanceof HTMLElement && surface.contains(active) ? active : undefined;
    keepColumnAnchor(place);
    if (!held) return;
    if (held.isConnected && !confirmNode) held.focus({ preventScroll: true });
    else refocusPanel();
  }

  /** The row to hold still: the selected one, or the first one in view. */
  function columnAnchor(): HTMLElement | undefined {
    const selected = column.querySelector<HTMLElement>(".selected");
    if (selected) return selected;
    for (const row of column.querySelectorAll<HTMLElement>(".items > li, .rows > li")) {
      if (row.getBoundingClientRect().bottom > 0) return row;
    }
    return undefined;
  }

  function keepColumnAnchor(change: () => void): void {
    const anchor = columnAnchor();
    const before = anchor?.getBoundingClientRect().top;
    // The browser's own scroll anchoring would correct the same shift a
    // second time, from a node of its own choosing.
    htmlRoot.style.overflowAnchor = "none";
    change();
    if (anchor?.isConnected && before !== undefined) {
      const shift = anchor.getBoundingClientRect().top - before;
      if (Math.abs(shift) >= 1) window.scrollBy({ top: shift, behavior: "instant" });
    }
    htmlRoot.style.removeProperty("overflow-anchor");
  }

  // A resize observer reports at most once a frame, however far a window's
  // edge is dragged, and `onLayout` re-places only when the answer changes.
  const layoutObserver = typeof ResizeObserver === "function" ? new ResizeObserver(() => relayout()) : undefined;
  layoutObserver?.observe(workspace);
  layoutObserver?.observe(probe);

  // ---- the plus -----------------------------------------------------------

  /**
   * The one creation trigger a screen has, and the shell owns the node.
   *
   * It used to point at a compose field the screen already had — the floating
   * Add put the caret in the list's inline form, and the rail's New list in
   * the index's. Both forms are going (I01, L01): the plus opens a popup now,
   * and what it opens is the screen's business, so the screen hands over an
   * action and the shell draws the button.
   *
   * One node, moved, rather than one per placement: I01 says the heading plus
   * and the floating plus are never shown together, and a single element makes
   * that true for a screen reader as well as for the stylesheet — there is
   * exactly one control named "Add item" in the tree at any width — and gives
   * the popup one place to send the focus back to.
   */
  const action = el("button", {
    class: "primary-action",
    attrs: { type: "button", hidden: true },
    children: [icon("plus")],
    on: { click: () => current?.activate() },
  });
  let screen: Screen | undefined;
  let current: ScreenAction | undefined;
  const railWide = window.matchMedia(RAIL);

  function placeAction(): void {
    current = screen?.action?.();
    if (current) {
      action.setAttribute("aria-label", current.label);
      action.setAttribute("title", current.label);
    }

    // Moving a node takes the focus off it, and a keyboard user who had just
    // reached the plus when the window was resized would be dropped on body.
    const held = document.activeElement === action;
    if (current && railWide.matches) {
      if (action.parentElement !== current.slot) current.slot.append(action);
      action.dataset["placement"] = "heading";
    } else {
      // Back into the frame, where the stylesheet fixes it above the bottom
      // navigation. Before the dialogs, so the DOM order of the frame's fixed
      // pieces stays what it was.
      if (action.parentElement !== node) node.insertBefore(action, dialog);
      action.dataset["placement"] = "floating";
    }
    if (held) action.focus({ preventScroll: true });
    syncActionHidden();
  }

  /**
   * S04: hidden while a dialog or sheet is up. A floating control over a
   * modal surface is a target nobody can reach and a shadow over the sheet's
   * footer. The three surfaces the shell owns are checked here; the screen's
   * own List options dialog is covered by a `:has(dialog[open])` rule in
   * `shell.css`, because the shell has no hook into it.
   */
  function syncActionHidden(): void {
    action.hidden = !current || dialog.open || popupDialog.open;
  }

  // The other half of S04: a phone's keyboard comes up under a focused field,
  // and the floating plus would sit on top of the line being typed. Marked on
  // the frame; the stylesheet hides the floating plus on the
  // mark. Only for a control that opens a keyboard -- ticking a checkbox
  // focuses an `<input>` too, and used to hide the plus under the thumb.
  const onColumnFocus = (event: FocusEvent) => {
    const typing = event.type === "focusin" && opensKeyboard(event.target instanceof Element ? event.target : null);
    node.dataset["typing"] = String(typing);
  };
  column.addEventListener("focusin", onColumnFocus);
  column.addEventListener("focusout", onColumnFocus);

  railWide.addEventListener("change", placeAction);

  // The same breakpoint, told to screens: below it a phone's rows drop their
  // drag column and reorder in an explicit mode (g53).
  const narrowWatchers = new Set<(narrow: boolean) => void>();
  const onRail = () => {
    for (const listener of [...narrowWatchers]) listener(!railWide.matches);
  };
  railWide.addEventListener("change", onRail);

  // ---- the popup host -----------------------------------------------------

  /**
   * The second surface kind: a form opened over the screen.
   *
   * The same `<dialog>` treatment as Details — `shell.css` makes it a centred
   * dialog from 768 and a bottom sheet below, and the head, the title and the
   * 44px Close are the same nodes by class (U03) — but its own element, because
   * the two differ in the one way that matters: Details is a column of the
   * page where there is room, and a form to add something is modal at every
   * width.
   *
   * Modal means Back closes it, so opening one spends a history entry on the
   * same URL. The same URL, deliberately: the router answers a popstate that
   * lands on the route it is already on with `update`, not a remount, so the
   * screen under the popup is never rebuilt by its own Back.
   */
  const popupTitle = el("h2", { class: "detail-title", attrs: { id: "popup-title" } });
  const closePopupButton = el("button", {
    class: "detail-close",
    attrs: { type: "button", "aria-label": "Close" },
    children: [icon("close")],
    on: { click: () => dismissPopup() },
  });
  const popupSlot = el("div", { class: "popup-slot" });
  const popupDialog = el("dialog", {
    class: "detail-dialog popup-dialog",
    attrs: { "aria-labelledby": "popup-title" },
    children: [
      el("div", {
        class: "detail-body",
        children: [
          el("div", { class: "detail-head", children: [popupTitle, closePopupButton] }),
          popupSlot,
        ],
      }),
    ],
  });

  interface OpenPopup {
    spec: PopupSpec;
    /** Whether the history entry this popup spent is still on the stack. */
    entry: boolean;
  }
  let popup: OpenPopup | undefined;
  // `close` fires for a dialog the host closed and for one the browser closed
  // on its own — see the listener. Only the second needs handling.
  let closingPopup = false;
  // The safe answer of the question over the popup, while one is up — the
  // same rule as the detail host's `confirmKeep`.
  let popupKeep: (() => void) | undefined;
  // As the detail host's `answered`: a cancel that answered a question, for
  // the close Chrome may fire after it.
  let popupAnswered = false;
  // Remember the edited field before a real press moves focus to a button.
  let lastPopupFocus: HTMLElement | undefined;
  popupSlot.addEventListener('focusin', event => {
    if (event.target instanceof HTMLElement && popup?.spec.body.contains(event.target))
      lastPopupFocus = event.target;
    if (event.target instanceof HTMLElement && event.target.matches('input, textarea, select')) lastDraftFocus = event.target;
  });

  function resumePopup(): void {
    if (!popup) return;
    popupKeep = undefined;
    popupSlot.replaceChildren(popup.spec.body);
    popupTitle.textContent = popup.spec.title;
    (lastPopupFocus?.isConnected ? lastPopupFocus : popup.spec.focus ?? firstControl(popup.spec.body))?.focus({ preventScroll: true });
  }

  function confirmPopup(spec: ConfirmSpec): void {
    cancelDeparture?.();
    if (!popup) return;
    const built = confirmPanel(spec);
    popupKeep = spec.keep.onChoose;
    popupSlot.replaceChildren(built.node);
    popupTitle.textContent = spec.title;
    built.focus.focus({ preventScroll: true });
  }

  /** A step of the popup's own task, held like a question: see `Popup.step`. */
  function stepPopup(spec: PopupStep): void {
    cancelDeparture?.();
    if (!popup) return;
    popupKeep = spec.cancel;
    popupSlot.replaceChildren(spec.body);
    popupTitle.textContent = spec.title;
    (spec.focus ?? firstControl(spec.body))?.focus({ preventScroll: true });
  }

  /** The guarded way out. A question already up is answered the safe way instead. */
  function dismissPopup(): void {
    if (!popup || popup.spec.busy?.()) return;
    if (popupKeep) {
      popupKeep();
      return;
    }
    const guard = popup.spec.guard;
    if (guard?.dirty()) {
      confirmPopup({
        title: guard.title,
        message: guard.message,
        keep: { label: guard.keep, onChoose: resumePopup },
        act: { label: guard.discard, onChoose: closePopup },
      });
      return;
    }
    closePopup();
  }

  /**
   * Takes the surface down without touching history or the focus. For a
   * popup being replaced by another, and for a screen being unmounted under
   * one: in both, a `history.back()` here would eat a navigation that is
   * already under way.
   */
  /**
   * Where a popup was, for a moment after it closes. A double tap on Add or
   * Cancel closes the sheet on its first half, and the second half lands on
   * whatever the sheet was covering — a checkbox it would tick, a row it
   * would open. A second press on the same spot in that moment does
   * nothing: a double tap lands twice where the finger is, so anything
   * further than half a target away is a new, deliberate press. Only after
   * a close a pointer made, and only a pointer's press: Enter has no second
   * half, and a click from the keyboard has no position and is always meant.
   */
  let justClosed: { x: number; y: number; until: number } | undefined;
  let lastPopupClick = { x: 0, y: 0 };
  // Half the 48px control: a second press closer than this is the same tap.
  const SAME_SPOT = 24;
  // True while a click a pointer made is being dispatched in the popup: the
  // close it causes happens inside that dispatch. Enter's implicit submit
  // clicks too, with no pointer (`detail` 0).
  let pointerClick = false;
  popupDialog.addEventListener("click", (event) => {
    pointerClick = event.detail > 0;
    lastPopupClick = { x: event.clientX, y: event.clientY };
    setTimeout(() => {
      pointerClick = false;
    });
  }, { capture: true });

  function dropPopup(): OpenPopup | undefined {
    cancelDeparture?.();
    const open = popup;
    if (!open) return undefined;
    justClosed = pointerClick ? { ...lastPopupClick, until: performance.now() + CONFIRM_INERT_MS } : undefined;
    // Said on the root while it lasts, so a browser driver can wait for it
    // to pass rather than for a length of time.
    if (justClosed) {
      node.dataset.pressGuard = "";
      setTimeout(() => delete node.dataset.pressGuard, CONFIRM_INERT_MS);
    }
    popup = undefined;
    popupKeep = undefined;
    popupAnswered = false;
    closingPopup = true;
    popupDialog.close();
    closingPopup = false;
    popupSlot.replaceChildren();
    open.spec.onClose?.();
    return open;
  }

  /** The unguarded way out. */
  function closePopup(): void {
    const open = dropPopup();
    if (!open) return;

    if (open.entry) {
      open.entry = false;
      // Unless the act spent the entry itself: a confirmed delete's
      // `dismiss` can replace the URL under the question before the surface
      // comes down, and a back for an entry that is no longer the popup's
      // would leave the screen rather than close the question.
      if ((window.history.state as { popup?: boolean } | null)?.popup) {
        back();
      }
    }

    // A Details sheet parked under this one comes back — see `openPopup`.
    place();
    syncActionHidden();

    if (dialog.open) {
      focusPanel();
      return;
    }
    // The trigger, or the plus, or the heading: I06 in order. `preventScroll`
    // throughout, because from 768 up the plus is beside the heading and a
    // plain `focus()` after adding to the end of a long list would scroll the
    // page to the top, away from the row that was just revealed.
    const asked = open.spec.returnFocusTo;
    const candidates: (HTMLElement | null | undefined)[] = [
      typeof asked === "function" ? asked() : asked,
      action.hidden ? undefined : action,
      column.querySelector<HTMLElement>("h1"),
    ];
    for (const candidate of candidates) {
      if (!candidate?.isConnected) continue;
      focusQuietly(candidate);
      return;
    }
  }

  /** A heading is not a tab stop, but it can be given the focus once. */
  function focusQuietly(target: HTMLElement): void {
    if (target.tagName === "H1" && !target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
    }
    target.focus({ preventScroll: true });
  }

  function openPopup(spec: PopupSpec, historyEntry = true): Popup {
    keyboard.reset();
    // One at a time. The one already up goes, and the new one inherits the
    // entry it spent rather than spending a second.
    const replaced = dropPopup();
    const entry = replaced?.entry ?? false;

    // Never two stacked. A modal Details is *parked* rather than dismissed:
    // its dismissal goes through `history.back()`, which lands later and
    // would pop this popup's own entry instead of the panel's. So the sheet
    // is closed silently — its panel, its `onDismiss` and the URL all
    // untouched — and `place()` brings it back when this closes. Docked,
    // Details is a column of the page and is simply left alone.
    if (dialog.open) closeDialog();

    popup = { spec, entry };
    lastPopupFocus = undefined;
    popupSlot.replaceChildren(spec.body);
    popupTitle.textContent = spec.title;
    if (!entry && historyEntry) {
      pushEntry(window.location.href, { popup: true });
      popup.entry = true;
    }
    if (!popupDialog.open) popupDialog.showModal?.();
    (spec.focus ?? firstControl(spec.body))?.focus({ preventScroll: true });
    syncActionHidden();

    const mine = popup;
    return {
      isOpen: () => popup === mine,
      dismiss: () => {
        if (popup === mine) dismissPopup();
      },
      close: () => {
        if (popup === mine) closePopup();
      },
      confirm: (next) => {
        if (popup === mine) confirmPopup(next);
      },
      resume: () => {
        if (popup === mine) resumePopup();
      },
      step: (next) => {
        if (popup === mine) stepPopup(next);
      },
    };
  }

  /**
   * A question with no form under it: the popup host, holding only the
   * confirmation. Every way out but the red button is the safe answer —
   * Cancel, Escape, the backdrop, Back — and there is one of it, `onClose`,
   * so none can disagree. The act runs *before* the surface comes down, so
   * that a `returnFocusTo` read at close sees what the act left: the row is
   * already gone, and the resolver picks its neighbour.
   */
  /** The standalone question up now, if one is; see `confirmDetail`. */
  let alone: Popup | undefined;

  function confirmAlone(spec: ConfirmSpec, options: ConfirmOptions = {}): Asked {
    let acted = false;
    let handle: Popup | undefined;
    const built = confirmPanel({
      title: spec.title,
      message: spec.message,
      keep: { label: spec.keep.label, onChoose: () => handle?.close() },
      act: {
        label: spec.act.label,
        danger: spec.act.danger,
        onChoose: () => {
          acted = true;
          // The action has been accepted. Navigation it performs must not be
          // mistaken for an attempt to leave an unanswered question.
          if (alone === handle) alone = undefined;
          spec.act.onChoose();
          handle?.close();
        },
      },
    });
    handle = openPopup({
      title: spec.title,
      body: built.node,
      focus: built.focus,
      returnFocusTo: options.returnFocusTo,
      onClose: () => {
        if (alone === handle) alone = undefined;
        if (!acted) spec.keep.onChoose();
      },
    });
    alone = handle;
    // For the screen that asked: the row the question is about can be
    // deleted elsewhere while it stands, and a question naming a row that
    // is gone has to go too.
    return { close: () => handle?.close() };
  }

  // Escape. Through `cancel` rather than `close`, because `cancel` is the one
  // that can be prevented: a dialog holding a draft says no and stays up.
  popupDialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    popupAnswered = popupKeep !== undefined;
    dismissPopup();
  });

  /**
   * A close the host did not make.
   *
   * Chrome honours `preventDefault` on `cancel` only when there has been user
   * activation since the last one, so a second Escape in a row closes the
   * dialog regardless. The draft, or the question, is still worth keeping:
   * the surface comes straight back up as `cancel` left it — asked, or
   * answered — since `cancel` fires first either way. See the detail host's.
   */
  popupDialog.addEventListener("close", () => {
    syncActionHidden();
    if (closingPopup || !popup) return;
    const keep = popup.spec.busy?.() || popup.spec.guard?.dirty() || popupKeep !== undefined || popupAnswered;
    popupAnswered = false;
    if (keep) {
      popupDialog.showModal?.();
      const question = popupSlot.querySelector<HTMLElement>(".confirm button");
      (question ?? popup.spec.focus ?? firstControl(popup.spec.body))?.focus({ preventScroll: true });
      return;
    }
    closePopup();
  });

  // The backdrop is the dialog element itself: everything inside is a child.
  popupDialog.addEventListener("click", (event) => {
    if (event.target === popupDialog) dismissPopup();
  });

  // The router restores the original entry before presenting a local question.
  // Cross-screen requests belong to main's combined departure guard.
  const stopGuard = addNavigationGuard(navigation => {
    if (navigation.own || screenKey(navigation.from) !== screenKey(navigation.to)) return;
    // Through the router's reading, not `itemId`: a note page's item is not
    // a selection, and only its Edit is (g77).
    const selected = selection;
    if (popup) {
      if (!navigation.traversal) return;
      if (popup.spec.busy?.()) return (_proceed, cancel) => { cancel(); };
      if (popupKeep || alone?.isOpen() || confirmKeep) return (_proceed, cancel) => { cancelQuestion(); cancel(); };
      // A traversal can jump past the popup and replace the underlying editor.
      // Its draft must be included even when the popup itself is untouched.
      if (panel && guard?.dirty() && selected(navigation.from) !== selected(navigation.to))
        return (proceed, cancel) => askDeparture(proceed, cancel);
      const draft = popup.spec.guard;
      if (draft && (popup.spec.navigationDirty?.() ?? draft.dirty())) return (proceed, cancel) => {
        const keep = () => { resumePopup(); cancel(); };
        confirmPopup({ title: draft.title, message: draft.message,
          keep: { label: draft.keep, onChoose: keep },
          act: { label: draft.discard, onChoose: proceed } });
        return keep;
      };
      return;
    }
    if (!navigation.traversal && selected(navigation.to) !== undefined) return;
    if (!panel || selected(navigation.from) === selected(navigation.to)) return;
    if (detail.asking()) return (_proceed, cancel) => { dismissDetail(); cancel(); };
    if (guard?.dirty()) return (proceed, cancel) => {
      const keep = () => { resumeDetail(); cancel(); };
      confirmDetail({ title: guard!.title, message: guard!.message,
        keep: { label: guard!.keep, onChoose: keep },
        act: { label: guard!.discard, onChoose: proceed } });
      return keep;
    };
  });
  const stopNavigation = onNavigationCommit(navigation => {
    if (!popup || !navigation.traversal) return;
    popup.entry = false;
    closePopup();
  });

  function cancelQuestion(): void {
    if (popupKeep || alone?.isOpen()) dismissPopup();
    else if (confirmKeep) dismissDetail();
  }

  function askDeparture(proceed: () => void, cancel: () => void): () => void {
    const focused = lastDraftFocus?.isConnected ? lastDraftFocus :
      document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    const field = focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement ? focused : undefined;
    const selection = field ? [field.selectionStart, field.selectionEnd] as const : undefined;
    let resume: () => void;
    let answered = false;
    const keep = () => {
      if (answered) return;
      answered = true;
      cancelDeparture = undefined;
      node.removeEventListener('keydown', escape);
      resume();
      if (focused?.isConnected) focused.focus({ preventScroll: true });
      if (field?.isConnected && selection?.[0] != null && selection[1] != null)
        field.setSelectionRange(selection[0], selection[1]);
      cancel();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing) return;
      event.preventDefault(); event.stopPropagation(); keep();
    };
    node.addEventListener('keydown', escape);
    const spec: ConfirmSpec = {
      title: 'Discard unsaved changes?',
      message: 'Your unsaved changes on this screen will be lost.',
      keep: { label: 'Keep editing', onChoose: keep },
      act: { label: 'Discard changes', onChoose: () => {
        if (!answered) {
          answered = true;
          cancelDeparture = undefined;
          node.removeEventListener('keydown', escape);
          proceed();
        }
      } },
    };
    if (popup) {
      resume = resumePopup;
      confirmPopup(spec);
    } else if (panel) {
      resume = resumeDetail;
      confirmDetail(spec);
    } else {
      const built = confirmPanel(spec);
      const question = openPopup({ title: spec.title, body: built.node, focus: built.focus,
        returnFocusTo: focused }, false);
      resume = () => question.close();
      popupKeep = keep;
    }
    cancelDeparture = keep;
    return keep;
  }

  // ---- the rail and the bottom navigation ---------------------------------

  // The compact line beside the gear. Its detail is for screen readers; the
  // Settings page says the whole thing.
  const railSync = syncLine(store, "rail");

  /**
   * Settings, at the foot of the rail (g48). A gear alone, because the rail's
   * height belongs to lists: the account, the theme and the release that sat
   * here as a stack of buttons are on the page it opens. A link rather than a
   * button, so the router's `data-link` handling gives it what every rail
   * entry has -- the draft guards, the held blur, a new tab on a middle click.
   *
   * The tooltip is the visible name, shown on hover and on keyboard focus, and
   * inside the link so a pointer moving onto it keeps it up. Escape hides it
   * until the pointer or the focus moves on (WCAG 1.4.13).
   */
  const railTip = el("span", { class: "rail-tip", text: "Settings", attrs: { "aria-hidden": "true" } });
  const railSettings = el("a", {
    class: "rail-settings",
    attrs: { href: "/settings", "data-link": true, "aria-label": "Settings" },
    children: [icon("gear"), railTip],
  });
  railSettings.addEventListener("keydown", (event) => {
    if (event.key === "Escape") railSettings.dataset.tip = "off";
  });
  // Back when the focus leaves, or the pointer leaves or comes back: a link
  // pressed with the mouse can keep the focus here (the router holds blur for
  // `data-link`), so a later hover must not find the name still put away.
  for (const type of ["blur", "mouseleave", "mouseenter"] as const)
    railSettings.addEventListener(type, () => delete railSettings.dataset.tip);

  /**
   * The Settings tab, and the only sync signal a phone shows without opening
   * anything. The mark is absent unless something of this device's is unsent —
   * see `syncMark`. Its accessible name carries the state, because the glyph
   * itself is `aria-hidden` and a mark nobody can hear is decoration.
   */
  const settingsMark = el("span", { class: "nav-mark", attrs: { hidden: true } });
  const settingsState = el("span", { class: "visually-hidden" });
  const settingsTab = el("a", {
    class: "nav-btn",
    attrs: { href: "/settings", "data-link": true },
    children: [
      el("span", { class: "nav-glyph", children: [icon("gear"), settingsMark] }),
      el("span", { text: "Settings" }),
      settingsState,
    ],
  });

  /**
   * The short window's navigation trigger (g55): where a landscape phone keeps
   * the rail, in a drawer this opens. Named, never an anonymous icon; the
   * accessible name says what the drawer holds, and it carries the same sync
   * mark as the Settings tab, since with the rail put away nothing else on
   * the screen shows that something is unsent. See `onNav`.
   */
  const triggerMark = el("span", { class: "nav-mark", attrs: { hidden: true } });
  const navTrigger = el("button", {
    class: "nav-trigger",
    attrs: { type: "button", "aria-expanded": "false", "aria-haspopup": "dialog", "aria-label": "Lists and navigation" },
    children: [el("span", { class: "nav-glyph", children: [icon("list"), triggerMark] }), el("span", { text: "Lists" })],
  });

  function paintMark(): void {
    const state = store.syncState();
    // No `status() === "ready"` gate, unlike the line. The line needs one
    // because "Synced" is a claim that must not be made before a pass has
    // answered; the mark never says that — it says "something of yours is
    // unsent", which is as true on a device that has never reached the server
    // as on one that has. A gate here would only be symmetry, and it would be
    // a branch nothing can reach.
    const mark = syncMark(state);
    settingsMark.replaceChildren(...(mark ? [icon(mark)] : []));
    // Assigned whole, so nothing else may set a class on this node and expect
    // it to survive — the same rule `rowPresentation` keeps for a row.
    settingsMark.className = mark === "alert" ? "nav-mark nav-mark-wrong" : "nav-mark";
    settingsMark.hidden = mark === undefined;
    // Read out as part of the tab's name — "Settings, Saved on this device" —
    // so the state is reachable without opening the page to look for it.
    //
    // The detail travels with it. The glyph is what separates an ordinary
    // pending queue from a failed one for everybody who can see it, and it is
    // `aria-hidden`; without this, both read as "Settings, Saved on this
    // device" and there is nothing to say anything went wrong.
    const words = mark === undefined ? undefined : syncWords(state);
    settingsState.textContent =
      words === undefined ? "" : [words.headline, words.detail].filter(Boolean).join(". ");
    triggerMark.replaceChildren(...(mark ? [icon(mark)] : []));
    triggerMark.className = settingsMark.className;
    triggerMark.hidden = mark === undefined;
    navTrigger.setAttribute("aria-label",
      settingsState.textContent ? `Lists and navigation, ${settingsState.textContent}` : "Lists and navigation");
  }

  const stopMark = store.onSyncState(paintMark);
  paintMark();

  /**
   * Lists, Today and Upcoming, above the individual lists.
   *
   * They are destinations rather than lists, so they are their own group with
   * its own markup: a rail entry is reconciled against `store.lists()` and
   * these are not in it. Only Today carries a count — Upcoming's is unbounded,
   * and a three-digit badge beside "Upcoming" is noise rather than information.
   */
  const listsLink = el("a", {
    class: "nav-btn",
    attrs: { href: "/", "data-link": true },
    children: [icon("list"), el("span", { class: "rail-name", text: "Lists" })],
  });
  const todayCount = countBadge();
  const todayLink = el("a", {
    class: "nav-btn",
    attrs: { href: agendaPath("today"), "data-link": true },
    children: [icon("calendar"), el("span", { class: "rail-name", text: "Today" }), todayCount],
  });
  const upcomingLink = el("a", {
    class: "nav-btn",
    attrs: { href: agendaPath("upcoming"), "data-link": true },
    children: [icon("calendarNext"), el("span", { class: "rail-name", text: "Upcoming" })],
  });
  const railAgenda = el("ul", {
    class: "rail-agenda",
    children: [
      el("li", { children: [listsLink] }),
      el("li", { children: [todayLink] }),
      el("li", { children: [upcomingLink] }),
    ],
  });

  const railLists = el("ul", { class: "rail-lists" });
  interface RailEntry {
    node: HTMLLIElement;
    link: HTMLAnchorElement;
    count: HTMLElement;
    /** The kind the glyph was drawn for, so a kind change swaps the glyph. */
    kind: ListKind;
  }
  const railNodes = new Map<string, RailEntry>();
  let currentListId: string | undefined;
  let route: Route | undefined;

  function openCounts(): { perList: Map<string, number>; today: number } {
    const items = store.allItems();
    const boundary = todayIso();
    let today = 0;
    for (const item of items) {
      // The same rule the list entries beside it use — what is *left* — so
      // ticking something off makes the badge go down, which is its whole job.
      // It deliberately leaves out the items already checked today, which the
      // projection itself still shows.
      if (!item.done && !(item.repeat && item.lastDoneOn === boundary) && inToday(item, boundary)) today += 1;
    }
    return { perList: uncheckedByList(items), today };
  }

  function railEntry(list: List): RailEntry {
    const count = countBadge();
    const label = el("span", { class: "rail-name" });
    const link = el("a", {
      class: "nav-btn",
      attrs: { href: listPath(list.id), "data-link": true },
      // Glyph, name, badge: one column each, and the same three the index's
      // rows have, so the two surfaces read as the same list (S02).
      children: [icon(KIND_ICON[list.kind]), label, count],
    });
    return { node: el("li", { children: [link] }), link, count, kind: list.kind };
  }

  const railEmpty = el("p", {
    class: "rail-empty",
    text: "No lists yet.",
    attrs: { hidden: true },
  });

  /**
   * Reconciled rather than rebuilt.
   *
   * A count changes every time anything anywhere is ticked, and replacing the
   * rail's children on each one would take the focus out of whichever rail link
   * a keyboard user had reached — and, on a touch device, move the link under
   * the finger that is on its way down.
   */
  function paintRail(): void {
    // Nothing to paint on a page with no rail (g72).
    if (standalone) return;
    const lists = store.lists();
    const { perList: counts, today: dueToday } = openCounts();

    setCount(todayCount, dueToday);
    todayLink.setAttribute("aria-label", `Today, ${uncheckedPhrase(dueToday)}`);
    for (const [name, link] of [
      ["lists", listsLink],
      ["today", todayLink],
      ["upcoming", upcomingLink],
    ] as const) {
      if (route?.name === name) link.setAttribute("aria-current", "page");
      else link.removeAttribute("aria-current");
    }

    for (const [id, entry] of railNodes) {
      if (lists.some((list) => list.id === id)) continue;
      entry.node.remove();
      railNodes.delete(id);
    }

    lists.forEach((list, index) => {
      let entry = railNodes.get(list.id);
      if (!entry) {
        entry = railEntry(list);
        railNodes.set(list.id, entry);
      }

      const open = counts.get(list.id) ?? 0;
      entry.link.querySelector(".rail-name")!.textContent = list.name;
      if (entry.kind !== list.kind) {
        entry.link.querySelector(".icon")?.replaceWith(icon(KIND_ICON[list.kind]));
        entry.kind = list.kind;
      }
      setCount(entry.count, open);
      // "Errands, 3 unchecked items" rather than a name and a loose number
      // beside it.
      entry.link.setAttribute("aria-label", listLabel(list, open));
      entry.link.setAttribute("href", listPath(list.id));
      if (list.id === currentListId) entry.link.setAttribute("aria-current", "page");
      else entry.link.removeAttribute("aria-current");

      if (railLists.children[index] !== entry.node) {
        const focused = entry.node.contains(document.activeElement) ? document.activeElement as HTMLElement : undefined;
        railLists.insertBefore(entry.node, railLists.children[index] ?? null);
        focused?.focus({ preventScroll: true });
      }
    });

    railEmpty.hidden = lists.length > 0;
  }

  /**
   * Below the account's lists, the shared lists it added (g137), headed as
   * Lists' own group is. Only those that still open: the rail goes places,
   * and one whose link ended goes nowhere. Redrawn only when they or the
   * current one change, with the focus kept on the same entry.
   */
  const railAdded = el("ul", { class: "rail-lists rail-added" });
  const railAddedSection = el("div", {
    class: "rail-section rail-added-section",
    attrs: { hidden: true },
    children: [el("p", { class: "rail-label", text: SHARED_WITH_ME.heading }), railAdded],
  });
  let paintedAdded: string | undefined;
  function paintAdded(): void {
    if (standalone) return;
    const live = store.addedLists().filter((entry) => entry.state === "live");
    const current = route?.name === "shared" ? route.grant : undefined;
    const said = JSON.stringify([live, current]);
    if (said === paintedAdded) return;
    paintedAdded = said;
    const focused = railAdded.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset["grant"] : undefined;
    railAdded.replaceChildren(...live.map((entry) => {
      const link = el("a", {
        class: "nav-btn",
        attrs: { href: addedPath(entry.grantId), "data-link": true, "data-grant": entry.grantId },
        children: [icon(entry.kind ? KIND_ICON[entry.kind] : "list"), el("span", { class: "rail-name", text: entry.name ?? SHARED_WITH_ME.unnamed })],
      });
      if (entry.grantId === current) link.setAttribute("aria-current", "page");
      return el("li", { children: [link] });
    }));
    if (focused) railAdded.querySelector<HTMLElement>(`[data-grant="${focused}"]`)?.focus({ preventScroll: true });
    railAddedSection.hidden = live.length === 0;
  }

  const userWatchers = new Set<(user: User) => void>();
  function updateUser(user: User) {
    if (user.id !== deps.user.id) return;
    deps.user.email = user.email;
    // The name the owner's share dialog and Settings read (g73). A user from
    // an older build carries none, and that says nothing about the account.
    if (user.name !== undefined) deps.user.name = user.name;
    rememberUser(deps.user);
    for (const watcher of userWatchers) watcher(deps.user);
  }
  // The guards the account buttons carried when they were the rail foot's,
  // unchanged: a form over the page, a question in Details, or a Details
  // draft that deleting the account would take with it.
  const formOpen = () => popup !== undefined || detail.asking();
  const account: AccountFacet = {
    updateUser,
    onUser(fn) {
      userWatchers.add(fn);
      return () => userWatchers.delete(fn);
    },
    deleteAccount: () => deps.deleteAccount?.() ?? Promise.reject(new Error("Account deletion is unavailable.")),
    formOpen,
    draftOpen: () => formOpen() || (guard?.dirty() ?? false),
  };

  const rail = el("nav", {
    class: "rail",
    attrs: { "aria-label": "Lists" },
    children: [
      el("a", {
        class: "wordmark",
        attrs: { href: "/", "data-link": true },
        // The chip rides the wordmark (g93), so the rail and the drawer, which
        // is this same node, both carry it and the link reads "Trayflux Test".
        children: [logo(), el("span", { text: "Trayflux" }), ...environmentChip("wordmark")],
      }),
      railAgenda,
      // The label and the lists it names, as one block (g55): the rail's
      // gap separates blocks, and the section's own, smaller gap ties the
      // label to its pane.
      el("div", {
        class: "rail-section",
        children: [el("p", { class: "rail-label", text: "Your lists" }), railEmpty, railLists],
      }),
      railAddedSection,
      // One row: the sync state and the gear. Nothing in it is rebuilt by a
      // sync or a list change, so neither can move the focus or a drag.
      el("div", { class: "rail-foot", children: [railSync.node, railSettings] }),
    ],
  });

  // ---- the phone bottom navigation ----------------------------------------
  //
  // There is no top bar. It held a wordmark and the second theme switch, and
  // neither earned 44px across the top of every phone screen: every screen
  // draws its own `<h1>`, and installed as a PWA the app's name is already in
  // the task switcher. What it held went to a More sheet, and from there to
  // the Settings page (g48), which the fourth tab now opens.

  // Every tab's icon in the same box the Settings gear already had, so the
  // four tabs are built alike and a mark could ride any of them.
  const glyph = (name: Parameters<typeof icon>[0]) => el("span", { class: "nav-glyph", children: [icon(name)] });

  const listsTab = el("a", {
    class: "nav-btn",
    attrs: { href: "/", "data-link": true },
    children: [glyph("list"), el("span", { text: "Lists" })],
  });

  const todayTab = el("a", {
    class: "nav-btn",
    attrs: { href: agendaPath("today"), "data-link": true },
    children: [glyph("calendar"), el("span", { text: "Today" })],
  });
  const upcomingTab = el("a", {
    class: "nav-btn",
    attrs: { href: agendaPath("upcoming"), "data-link": true },
    children: [glyph("calendarNext"), el("span", { text: "Upcoming" })],
  });

  // The rail's order, Lists first (g52): Lists is where the app lands, and a
  // phone that put it third taught a second map of the same four places.
  const bottomNav = el("nav", {
    class: "bottom-nav",
    attrs: { "aria-label": "Sections" },
    children: [
      listsTab,
      todayTab,
      upcomingTab,
      settingsTab,
      // A phone has no wordmark on screen, so the chip sits on the bar's top
      // edge (g93). Out of flow, so the bar's measured height does not move.
      ...environmentChip("nav"),
    ],
  });

  // ---- the short window's drawer (g55) -------------------------------------
  //
  // A phone on its side is past the rail breakpoint, and at 844x390 the rail
  // took a quarter of the width and showed barely one list. Below
  // `--short-height` the rail is put away: a bar across the top holds a named
  // trigger, and the trigger opens the same rail node in a modal drawer. The
  // same node, moved as the detail panel is, so its links, counts, current
  // marker, sync line and Settings gear are the rail's own and nothing is
  // duplicated. Back is ordinary navigation: the drawer spends no history
  // entry, since one per opening would leave an extra Back stop after every
  // navigation made from it, and its open state is never kept.

  // The drawer hides the wordmark until it is opened, so the bar says it too.
  const topbar = el("header", { class: "topbar", children: [navTrigger, ...environmentChip("topbar")] });
  const drawerClose = el("button", {
    class: "detail-close drawer-close",
    attrs: { type: "button", "aria-label": "Close navigation" },
    children: [icon("close")],
    on: { click: () => closeDrawer() },
  });
  const drawer = el("dialog", {
    class: "nav-drawer",
    attrs: { "aria-label": "Navigation" },
    children: [drawerClose],
  });

  let navMode: "bar" | "rail" | "drawer" | undefined;

  function openDrawer(): void {
    if (navMode !== "drawer" || drawer.open) return;
    drawer.showModal?.();
    navTrigger.setAttribute("aria-expanded", "true");
    // Where the person is, rather than the Close button: the drawer's first
    // answer is "you are here".
    (rail.querySelector<HTMLElement>('[aria-current="page"]') ?? rail.querySelector<HTMLElement>("a[href]"))?.focus();
  }

  /** The browser puts the focus back on the trigger, which opened it. */
  function closeDrawer(): void {
    if (drawer.open) drawer.close();
  }

  navTrigger.addEventListener("click", openDrawer);
  // Escape closes it natively, through `cancel`; this keeps the trigger's
  // state true whichever way it went.
  drawer.addEventListener("close", () => navTrigger.setAttribute("aria-expanded", "false"));
  drawer.addEventListener("click", (event) => {
    // The backdrop: the rail fills the drawer, so a click on the dialog
    // element itself landed outside it.
    if (event.target === drawer) {
      closeDrawer();
      return;
    }
    // A destination: the drawer goes before the router acts, so a draft's
    // question is never stacked over it. A modified click opens a new tab
    // and leaves the drawer where it is.
    const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (link && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) closeDrawer();
  }, { capture: true });

  /**
   * Which navigation the window gets: the bar below the rail breakpoint, and
   * above it the rail, unless the window is shorter than the probe's
   * `--short-height`, where the drawer stands in for it. Height is read from
   * the layout viewport, which a browser may shrink for a keyboard, so while
   * a field holds the keyboard the answer is kept until the field lets go.
   */
  function onNav(): void {
    if (standalone) {
      node.dataset["nav"] = "none";
      return;
    }
    const next = !railWide.matches ? "bar" : window.innerHeight < probe.getBoundingClientRect().height ? "drawer" : "rail";
    if (next === navMode) return;
    if (navMode !== undefined && navMode !== "bar" && next !== "bar" && opensKeyboard(document.activeElement)) return;
    const active = document.activeElement;
    const heldLink = active instanceof HTMLElement && rail.contains(active) ? active : undefined;
    navMode = next;
    node.dataset["nav"] = next;
    if (next === "drawer") {
      if (rail.parentElement !== drawer) drawer.append(rail);
    } else {
      closeDrawer();
      if (rail.parentElement !== node) node.insertBefore(rail, topbar);
    }
    if (!heldLink) return;
    // Moving a node takes the focus off it. The same link where the rail is
    // on screen, the trigger where it went into the drawer, and the heading
    // on a phone, where the rail is not shown at all.
    if (next === "rail") heldLink.focus({ preventScroll: true });
    else if (next === "drawer") navTrigger.focus({ preventScroll: true });
    else {
      const heading = column.querySelector<HTMLElement>("h1");
      if (heading) focusQuietly(heading);
    }
  }

  /** Both of the frame's size questions, in the order they depend on each other. */
  function relayout(): void {
    onNav();
    onLayout();
  }

  // The freeze in `onNav` ends when the field lets go: a turn later, so a
  // focus moving from one field to the next is still one editing session.
  const onFocusOut = () => setTimeout(onNav);

  // ---- assembly -----------------------------------------------------------

  const node = el("div", {
    class: "app",
    children: standalone
      // No rail, bar or drawer here, so no wordmark to ride: the page a share
      // link opens takes the chip the screens outside the shell take (g93).
      ? [el("a", { class: "skip", text: "Skip to content", attrs: { href: "#main" } }), workspace, action, dialog, popupDialog, probe,
          ...environmentChip("page")]
      : [
          el("a", { class: "skip", text: "Skip to content", attrs: { href: "#main" } }),
          rail,
          topbar,
          workspace,
          bottomNav,
          action,
          dialog,
          popupDialog,
          drawer,
          probe,
        ],
  });
  if (standalone) node.dataset["nav"] = "none";

  node.addEventListener("focusout", onFocusOut);
  window.addEventListener("resize", relayout);
  railWide.addEventListener("change", relayout);

  /**
   * The top bar's height, for what has to clear it: the docked inspector,
   * which sticks below it, and focus scrolled into view. Written on the root
   * as `--nav-height` is; 0 wherever the bar is not shown.
   */
  const writeTopbarHeight = () => {
    htmlRoot.style.setProperty("--topbar-height", standalone ? "0px" : `${topbar.getBoundingClientRect().height}px`);
  };
  const topbarObserver = typeof ResizeObserver === "function" ? new ResizeObserver(writeTopbarHeight) : undefined;
  topbarObserver?.observe(topbar);

  const keyboard = keyboardViewport(node, column, () => !railWide.matches);

  // The other half of `justClosed`: a press where the popup just was.
  for (const type of ["pointerdown", "mousedown", "click"] as const) {
    node.addEventListener(type, (event) => {
      if (!justClosed || performance.now() > justClosed.until) return;
      if (type === "click" && (event as MouseEvent).detail === 0) return;
      const { clientX: x, clientY: y } = event as MouseEvent;
      if (Math.hypot(x - justClosed.x, y - justClosed.y) > SAME_SPOT) return;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true });
  }

  /**
   * The bar's rendered height, written where the floating furniture reads it
   * (g52). `--nav-height` in `product-tokens.css` is the first paint's
   * estimate; this is the measurement, safe area included, and it follows the
   * bar as its labels wrap at larger text. On the root element, because the
   * tokens derived from it (`--float-bottom`, `--page-clearance`) are declared
   * there and a value set lower down would not reach their `calc()`. The bar is
   * `display: none` from 768, where the observer reports 0.
   */
  const writeNavHeight = () => {
    // No bar at all on a page with nowhere to go (g72): the floating plus and
    // the page's clearance sit on the safe area alone.
    htmlRoot.style.setProperty("--nav-height", standalone ? "0px" : `${bottomNav.getBoundingClientRect().height}px`);
  };
  if (standalone) {
    writeNavHeight();
    writeTopbarHeight();
  }
  const navObserver = typeof ResizeObserver === "function" ? new ResizeObserver(writeNavHeight) : undefined;
  navObserver?.observe(bottomNav);
  const stopLists = store.onLists(paintRail);
  // Counts are the only thing an item change means to the rail, and it is the
  // whole rail's worth of them: a tick in one list cannot change another's.
  const stopItems = store.onAllItems(paintRail);
  const stopAdded = store.onAddedLists(paintAdded);
  let countsDay = todayIso();
  const refreshCalendarCounts = () => {
    const nextDay = todayIso();
    if (nextDay === countsDay) return;
    countsDay = nextDay;
    paintRail();
  };
  window.addEventListener("focus", refreshCalendarCounts);
  document.addEventListener("visibilitychange", refreshCalendarCounts);

  function setRoute(next: Route): void {
    // Wherever the person went, the drawer has done its job.
    closeDrawer();
    route = next;
    // A note page is inside its item's list (g77).
    currentListId = routeListId(next);

    // A list is inside the Lists section, so the phone's destination stays
    // marked while you are in one — there is no rail down there to say where
    // you are, and the design system's own navigation does the same
    // (`design/app.js`: mobile Lists is current whenever a list is the view).
    // A list someone shared, opened from Lists, is inside it too (g137).
    const inLists = next.name === "lists" || next.name === "shared" || currentListId !== undefined;
    if (inLists) listsTab.setAttribute("aria-current", "page");
    else listsTab.removeAttribute("aria-current");

    for (const [name, tab] of [
      ["today", todayTab],
      ["upcoming", upcomingTab],
      ["settings", settingsTab],
      ["settings", railSettings],
    ] as const) {
      // The account's Activity is reached from Settings, and stays inside it (g110).
      const here = next.name === name || (name === "settings" && next.name === "activity" && next.list === undefined);
      if (here) tab.setAttribute("aria-current", "page");
      else tab.removeAttribute("aria-current");
    }

    paintRail();
    paintAdded();
  }

  paintRail();
  paintAdded();

  return {
    node,
    updateUser,
    account,
    detail,
    announce,

    mount(next, route) {
      keyboard.reset();
      // A control on the outgoing screen held the focus — the List options
      // button, after deleting the list — and is about to be detached, which
      // leaves the focus on the body and a keyboard at the top of the page.
      // The new screen's heading is where it lands instead. Read before the
      // popup goes: the New list popup navigates here from inside itself,
      // and the field it held is gone once the popup is dropped.
      const focused = document.activeElement;
      // The drawer's trigger too (g55): a destination chosen in the drawer
      // closes it, which hands the focus back to the trigger, and the new
      // screen's heading is where that choice should land.
      const held = column.contains(focused) || popupDialog.contains(focused) ||
        drawer.contains(focused) || focused === navTrigger;
      // A popup over a screen that is being replaced is a popup over nothing.
      // Dropped, not closed: the navigation that got here is already in the
      // history, and a `back()` now would undo it.
      dropPopup();
      screen = next;
      column.replaceChildren(next.node);
      placeAction();
      setRoute(route);
      if (held) {
        const heading = next.node.querySelector<HTMLElement>("h1");
        if (heading) focusQuietly(heading);
      }
    },

    setRoute,
    lend,
    openPopup,
    confirm: confirmAlone,
    refreshAction: placeAction,
    narrow: () => !railWide.matches,
    onNarrow(listener) {
      narrowWatchers.add(listener);
      return () => narrowWatchers.delete(listener);
    },
    navigationDirty: () => detail.dirty() || (popup !== undefined &&
      (popup.spec.navigationDirty?.() ?? popup.spec.guard?.dirty() ?? false)),
    navigationBlocked: () => detail.asking() || popupKeep !== undefined ||
      (alone?.isOpen() ?? false) || (popup?.spec.busy?.() ?? false),
    cancelQuestion,
    askDeparture,

    // The popup's guard, its question, and anything typed into its form: a
    // popup's fields start empty, so a value in one was typed, and a guard
    // can have let go while the text is still on screen -- the Add popup's
    // does once its list is deleted, keeping the draft visible to copy.
    dirty: () =>
      detail.dirty() ||
      detail.asking() ||
      (popup !== undefined &&
        ((popup.spec.guard?.dirty() ?? false) || popupKeep !== undefined || holdsText(popupDialog))),

    dispose() {
      window.removeEventListener("focus", refreshCalendarCounts);
      document.removeEventListener("visibilitychange", refreshCalendarCounts);
      keyboard.dispose();
      navObserver?.disconnect();
      htmlRoot.style.removeProperty("--nav-height");
      dropPopup();
      stopLists();
      stopItems();
      stopAdded();
      giveBack();
      notices.dispose();
      categories.dispose();
      railSync.dispose();
      stopMark();
      userWatchers.clear();
      layoutObserver?.disconnect();
      topbarObserver?.disconnect();
      htmlRoot.style.removeProperty("--topbar-height");
      closeDrawer();
      node.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("resize", relayout);
      railWide.removeEventListener("change", relayout);
      railWide.removeEventListener("change", placeAction);
      railWide.removeEventListener("change", onRail);
      narrowWatchers.clear();
      stopGuard();
      stopNavigation();
    },
  };
}
